<template>
  <div class="client-list fade-in">

    <!-- === GLOBAL LOADER === -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- === KPI CARDS === -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(kpi, i) in clientStats" :key="i">
        <div>
          <h4>{{ kpi.title }}</h4>
          <div class="value">{{ kpi.value }}</div>
        </div>
        <i :class="kpi.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- === FILTERS & ACTIONS === -->
    <div class="grid-header fade-in">
      <div class="filters">

        <!-- Search -->
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search clients..."
            @input="fetchClients"
          />
        </div>

        <!-- Import Export Sample -->
        <div class="filter-actions">
          <button class="btn-outline" @click="triggerImport">
            <i class="bx bx-import"></i> Import
          </button>

          <button class="btn-outline" @click="exportClientsList">
            <i class="bx bx-export"></i> Export
          </button>

          <button class="btn-outline" @click="downloadSampleFile">
            <i class="bx bx-download"></i> Sample
          </button>
        </div>

      </div>

      <button class="btn-primary" @click="addClient">
        <i class="bx bx-plus"></i> Add Client
      </button>
    </div>

    <!-- === CLIENT CARDS GRID === -->
    <div v-if="filteredClients.length" class="website-grid fade-in">

      <div class="website-card" v-for="(client, i) in filteredClients" :key="i">

        <!-- Header -->
        <div class="website-card-header">
          <h4><i class="bx bx-user"></i> {{ client.name }}</h4>
        </div>

        <!-- Body -->
        <div class="website-card-body">
          <div class="item-info">

            <div class="info-item">
              <i class="bx bx-phone"></i>
              <span><strong>Phone:</strong> {{ client.contact }}</span>
            </div>

            <div class="info-item">
              <i class="bx bx-envelope"></i>
              <span><strong>Email:</strong> {{ client.email }}</span>
            </div>

            <div class="info-item">
              <i class="bx bx-map"></i>
              <span><strong>Address:</strong> {{ client.address }}</span>
            </div>

            <div class="info-item">
              <i class="bx bx-pulse"></i>
              <span><strong>Status:</strong>
                <span
                  :class="['status-pill', client.status === 'Active' ? 'active' : 'inactive']"
                >
                  {{ client.status }}
                </span>
              </span>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="website-card-footer">
          <span class="website-domain">Added on: {{ client.createdAt }}</span>

          <div class="footer-actions">
            <button class="btn-edit" @click="editClient(client)">
              <i class="bx bx-edit"></i> Edit
            </button>
            <button class="btn-delete" @click="confirmDelete(client)">
              <i class="bx bx-trash"></i> Delete
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- === EMPTY STATE === -->
    <div v-else class="empty-state fade-in">
      <i class="bx bx-user-x" style="font-size: 2rem; color: var(--primary);"></i>
      <p>No clients found. Add a new client to get started!</p>
      <button class="btn-primary mt-2" @click="addClient"
        style="padding: 8px 14px; font-size: 13px; border-radius: 6px;">
        <i class="bx bx-plus" style="font-size: 14px; color: white;"></i> Add New Client
      </button>
    </div>

    <!-- === PAGINATION === -->
    <div v-if="filteredClients.length" class="pagination fade-in">
      <span>Showing {{ filteredClients.length }} of {{ clients.length }} clients</span>
      <div class="pagination-buttons">
        <button><i class="bx bx-chevron-left"></i></button>
        <button class="active">1</button>
        <button>2</button>
        <button><i class="bx bx-chevron-right"></i></button>
      </div>
    </div>

    <!-- Confirm Dialog -->
    <ConfirmDialog
      :show="showConfirm"
      :title="confirmTitle"
      :message="confirmMessage"
      @cancel="showConfirm = false"
      @confirmAction="handleConfirm"
    />

    <!-- Hidden file input -->
    <input
      type="file"
      id="clientImportFile"
      accept=".csv"
      style="display:none"
      @change="handleImportFile"
    />

  </div>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import {
  getClients,
  deleteClient,
  importClients,
  exportClients,
  downloadClientSample,
  getClientReport
} from "@/apis/clientapis";

import ConfirmDialog from "@/comps/ConfirmDialog.vue";

const toast = useToast();
const loading = ref(false);
const searchQuery = ref("");
const clients = ref([]);

// KPI cards
const clientStats = ref([
  { title: "Total Clients", value: 0, icon: "bx bx-group" },
  { title: "Active", value: 0, icon: "bx bx-user-check" },
  { title: "Inactive", value: 0, icon: "bx bx-user-x" },
  { title: "Invoices", value: 0, icon: "bx bx-file" },
]);

// confirm dialog
const showConfirm = ref(false);
const confirmTitle = ref("");
const confirmMessage = ref("");
let confirmCallback = () => {};

const openConfirm = (title, message, action) => {
  confirmTitle.value = title;
  confirmMessage.value = message;
  confirmCallback = action;
  showConfirm.value = true;
};

const handleConfirm = () => {
  showConfirm.value = false;
  if (typeof confirmCallback === "function") confirmCallback();
};

// Fetch clients
async function fetchClients() {
  try {
    loading.value = true;
    const user = JSON.parse(sessionStorage.getItem("ldata")) || {};

    const res = await getClients(user.user_id, "", 0, searchQuery.value);

    if (res?.status && res.records) {
      clients.value = res.records.map((c) => ({
        id: c.user_id,
        name: `${c.fname || ""} ${c.lname || ""}`.trim(),
        contact: c.mobile || "-",
        email: c.email || "-",
        address: c.address_line1 || "-",
        status: c.status === "1" ? "Active" : "Inactive",
        createdAt: new Date(c.user_cre_datetime).toLocaleDateString(),
      }));
    }
  } catch {
    toast.error("Failed to load clients");
  } finally {
    loading.value = false;
  }
}

// KPI Report
async function loadClientReport() {
  const user = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getClientReport(user.user_id);

  if (res?.status) {
    clientStats.value = [
      { title: "Total Clients", value: res.total_clients, icon: "bx bx-group" },
      { title: "Active", value: res.active_clients, icon: "bx bx-user-check" },
      { title: "Inactive", value: res.inactive_clients, icon: "bx bx-user-x" },
      { title: "Invoices", value: res.total_invoices, icon: "bx bx-file" },
    ];
  }
}

// Import clients
function triggerImport() {
  document.getElementById("clientImportFile").click();
}

async function handleImportFile(e) {
  const file = e.target.files[0];
  if (!file) return;

  try {
    loading.value = true;
    const user = JSON.parse(sessionStorage.getItem("ldata")) || {};

    const res = await importClients(file, user.user_id);

    if (res?.status) {
      toast.success("Clients imported successfully!");
      fetchClients();
      loadClientReport();
    } else toast.error("Import failed");
  } finally {
    loading.value = false;
    e.target.value = "";
  }
}

// Export
async function exportClientsList() {
  const user = JSON.parse(sessionStorage.getItem("ldata")) || {};
  await exportClients(user.user_id);
  toast.success("Export started...");
}

// Sample Download
async function downloadSampleFile() {
  const res = await downloadClientSample();
  if (res?.status) toast.success("Sample downloaded!");
}

// Delete
function confirmDelete(client) {
  openConfirm("Delete Client", `Delete ${client.name}?`, async () => {
    const res = await deleteClient(client.id);
    if (res?.status) {
      toast.success("Client deleted");
      fetchClients();
    }
  });
}

// Navigation
function addClient() {
  window.location.href = "/new_client";
}
function editClient(client) {
  window.location.href = `/new_client?id=${client.id}`;
}

// Computed
const filteredClients = computed(() => {
  return clients.value.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

onMounted(() => {
  fetchClients();
  loadClientReport();
});
</script>
<style scoped>
/* === Animation === */
.fade-in {
  animation: fadeInUp .6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to { opacity: 1; transform: translateY(0); }
}

/* === KPI CARDS === */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(220px,1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: .3s ease;
  box-shadow: 0 2px 8px rgba(17,24,39,.05);
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17,24,39,.08);
}
.kpi-card h4 {
  font-size: .9rem;
  color: #6b7280;
}
.kpi-card .value {
  font-size: 1.4rem;
  font-weight: 700;
}
.kpi-icon {
  font-size: 1.8rem;
  color: var(--primary);
}

/* === Search Box === */
.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
}
.search-box input {
  border: none;
  outline: none;
  font-size: 14px;
}
.search-box i {
  color: var(--primary);
}

/* === Filters === */
.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}
.filter-actions {
  display: flex;
  gap: 10px;
}

/* ======================================================
   PRIMARY BUTTON (Add Client)
====================================================== */
.btn-primary {
  background: var(--primary, #111827);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

/* ======================================================
   OUTLINE BUTTON (Import / Export / Sample)
====================================================== */
.btn-outline {
  background: #fff;
  border: 1px solid var(--primary, #111827);
  color: var(--primary, #111827);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-outline:hover {
  background: var(--primary, #111827);
  color: #fff;
  border-color: var(--primary, #111827);
}

/* ======================================================
   EDIT BUTTON (Same as Item Edit)
====================================================== */
.btn-edit {
  background: var(--primary, #111827);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-edit:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}

/* ======================================================
   DELETE BUTTON (Same as Item Delete)
====================================================== */
.btn-delete {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-delete:hover {
  background: #fee2e2;
  color: #991b1b;
  transform: translateY(-1px);
}

/* ======================================================
   RESPONSIVE BUTTON FIXES
====================================================== */
@media (max-width: 600px) {
  .btn-primary,
  .btn-outline,
  .footer-actions button {
    width: 100% !important;
    justify-content: center !important;
  }
}

.website-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;
}
@media (max-width: 992px) {
  .website-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .website-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}


/* === Card === */
.website-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  padding: 1.2rem;
  transition: .3s;
}
.website-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17,24,39,.08);
}

/* Header */
.website-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .8rem;
}
.website-card-header h4 {
  font-size: 1.05rem;
  display: flex;
  gap: 6px;
}
.website-card-header i {
  color: var(--primary);
}

/* Item Info Grid */
.item-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.info-item {
  display: flex;
  gap: 6px;
  font-size: 13px;
}
.info-item i {
  color: var(--primary);
}

/* Status pill */
.status-pill {
  padding: .2rem .6rem;
  border-radius: 999px;
  font-size: .8rem;
  font-weight: 600;
}
.status-pill.active {
  background: #dcfce7;
  color: #166534;
}
.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* Footer */
.website-card-footer {
  margin-top: .8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
}
.btn-delete {
  padding: 6px 12px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #991b1b;
}

/* Empty State */
.empty-state {
  padding: 3rem;
  border: 2px dashed #e5e7eb;
  background: #f9fafb;
  border-radius: 12px;
  text-align: center;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  gap: 6px;
}
.pagination-buttons button {
  background: #f3f4f6;
  padding: 6px 10px;
  border-radius: 6px;
}
.pagination-buttons .active,
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* Loader */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.loader-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid var(--primary);
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ================================
   📱 RESPONSIVE GRID (MAX 3 → 2 → 1)
================================ */
.website-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;
}

/* Tablet - 2 cards */
@media (max-width: 992px) {
  .website-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

/* Mobile - 1 card */
@media (max-width: 600px) {
  .website-grid {
    grid-template-columns: 1fr !important;
  }
}

/* ================================
   📱 RESPONSIVE CARD PATCH
================================ */

/* Card padding reduced on mobile */
@media (max-width: 600px) {
  .website-card {
    padding: 1rem !important;
  }

  .website-card-header h4 {
    font-size: 1rem !important;
  }

  .info-item {
    font-size: 0.88rem !important;
  }
}

/* ================================
   📱 KPI CARDS RESPONSIVE
================================ */
@media (max-width: 992px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (max-width: 600px) {
  .kpi-row {
    grid-template-columns: 1fr !important;
  }
}

/* ================================
   📱 FILTER BAR RESPONSIVE
================================ */
@media (max-width: 768px) {
  .grid-header {
    flex-direction: column !important;
    gap: 1rem !important;
    align-items: stretch !important;
  }

  .filters {
    flex-direction: column !important;
    width: 100% !important;
    gap: 0.8rem !important;
  }

  .search-box {
    width: 100% !important;
  }

  .filter-actions {
    width: 100% !important;
    display: flex;
    gap: 0.6rem !important;
    flex-wrap: wrap !important;
    justify-content: space-between !important;
  }

  .filter-actions button {
    flex: 1 1 30% !important;
    font-size: 0.85rem !important;
  }

  .btn-primary {
    width: 100% !important;
    justify-content: center !important;
  }
}

/* ================================
   📱 FOOTER BUTTON RESPONSIVE
================================ */
@media (max-width: 600px) {
  .footer-actions {
    flex-direction: column !important;
    gap: 0.6rem !important;
  }

  .footer-actions button {
    width: 100% !important;
  }
}

/* ================================
   📱 PAGINATION RESPONSIVE
================================ */
@media (max-width: 992px) {
  .pagination {
    justify-content: center !important;
    text-align: center !important;
    gap: 0.8rem !important;
    flex-direction: column !important;
  }

  .pagination span {
    width: 100% !important;
    font-size: 0.9rem !important;
  }

  .pagination-buttons {
    flex-wrap: wrap !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .pagination-buttons button {
    min-width: 36px !important;
    height: 36px !important;
  }
}

@media (max-width: 480px) {
  .pagination-buttons button {
    flex: 1 1 22% !important;
    padding: 0.5rem !important;
    font-size: 0.85rem !important;
  }
}
/* ======================================================
   GLOBAL ANIMATION
====================================================== */
.fade-in {
  animation: fadeInUp .6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to { opacity: 1; transform: translateY(0); }
}

/* ======================================================
   KPI CARDS
====================================================== */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: .3s ease;
  box-shadow: 0 2px 8px rgba(17,24,39,.05);
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17,24,39,.08);
}
.kpi-card h4 {
  font-size: .9rem;
  color: #6b7280;
}
.kpi-card .value {
  font-size: 1.4rem;
  font-weight: 700;
}
.kpi-icon {
  font-size: 1.8rem;
  color: var(--primary);
}

/* ======================================================
   SEARCH BOX
====================================================== */
.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.search-box input {
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
}
.search-box i {
  color: var(--primary);
}

/* ======================================================
   FILTER ACTIONS
====================================================== */
.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-actions {
  display: flex;
  gap: 10px;
}

/* ======================================================
   BUTTONS
====================================================== */
.btn-outline {
  background: #fff;
  border: 1px solid var(--primary);
  color: var(--primary);
  padding: .6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  transition: .3s;
}
.btn-outline:hover {
  background: var(--primary);
  color: #fff;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-radius: 8px;
  padding: .6rem 1.2rem;
  font-weight: 600;
  transition: .3s;
}
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

/* ======================================================
   GRID (MAX 3 → 2 → 1)
====================================================== */
.website-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;
}

/* Tablet - 2 cards */
@media (max-width: 992px) {
  .website-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

/* Mobile - 1 card */
@media (max-width: 600px) {
  .website-grid {
    grid-template-columns: 1fr !important;
  }
}

/* ======================================================
   CARD STYLE
====================================================== */
.website-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  padding: 1.2rem;
  transition: .3s;
}
.website-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17,24,39,.08);
}

.website-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: .8rem;
}
.website-card-header h4 {
  font-size: 1.05rem;
  display: flex;
  gap: 6px;
}
.website-card-header i {
  color: var(--primary);
}

/* ======================================================
   CARD INFO GRID
====================================================== */
.item-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.info-item {
  display: flex;
  gap: 6px;
  font-size: 13px;
}
.info-item i {
  color: var(--primary);
}

/* ======================================================
   STATUS PILL
====================================================== */
.status-pill {
  padding: .2rem .6rem;
  border-radius: 999px;
  font-size: .8rem;
  font-weight: 600;
}
.status-pill.active {
  background: #dcfce7;
  color: #166534;
}
.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* ======================================================
   CARD FOOTER
====================================================== */
.website-card-footer {
  margin-top: .8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.footer-actions {
  display: flex;
  gap: 8px;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  transition: .3s;
}
.btn-edit:hover {
  background: #1f2937;
}
.btn-delete {
  padding: 6px 12px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #000000;
}

/* ======================================================
   EMPTY STATE
====================================================== */
.empty-state {
  padding: 3rem;
  border: 2px dashed #e5e7eb;
  background: #f9fafb;
  border-radius: 12px;
  text-align: center;
}
.empty-state i {
  margin-bottom: .5rem;
}

/* ======================================================
   PAGINATION
====================================================== */
.pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination-buttons {
  display: flex;
  gap: 6px;
}
.pagination-buttons button {
  background: #f3f4f6;
  padding: 6px 10px;
  border-radius: 6px;
  transition: .3s;
}
.pagination-buttons .active,
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* ======================================================
   GLOBAL LOADER
====================================================== */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}
.loader-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid var(--primary);
  border-radius: 50%;
  animation: spin .8s linear infinite;
  margin-bottom: 0.8rem;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ======================================================
   **MOBILE RESPONSIVE PATCH**
====================================================== */

/* Tablet filters */
@media (max-width: 768px) {
  .grid-header {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 1rem !important;
  }

  .filters {
    width: 100% !important;
    flex-direction: column !important;
    gap: .8rem !important;
  }

  .filter-actions {
    width: 100% !important;
    flex-wrap: wrap !important;
    justify-content: space-between !important;
  }

  .filter-actions button {
    flex: 1 1 30% !important;
  }

  .btn-primary {
    width: 100% !important;
  }
}

/* Mobile card adjustments */
@media (max-width: 600px) {
  .website-card {
    padding: 1rem !important;
  }
  .item-info {
    grid-template-columns: 1fr !important;
  }
  .footer-actions {
    flex-direction: column !important;
    width: 100% !important;
  }
  .footer-actions button {
    width: 100% !important;
  }
}

/* Pagination mobile */
@media (max-width: 600px) {
  .pagination {
    flex-direction: column !important;
    text-align: center !important;
    gap: .8rem !important;
  }
  .pagination-buttons {
    width: 100%;
    justify-content: center !important;
    flex-wrap: wrap;
  }
  .pagination-buttons button {
    flex: 1 1 22% !important;
  }
}

</style>
